import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import flags from 'react-phone-number-input/flags';
import { EmptyAnalyticsState } from './EmptyAnalyticsState';
import { CircleQuestionMark } from 'lucide-react';

/**
 * Breakdown of participants by geography and devices.
 * Dynamically updates based on the provided time window.
 */
export const OverviewDistributions: React.FC = () => {
  const { t } = useTranslation('analytics');
  const { geographic, technical } = useAppSelector((state) => state.analytics.overview);

  const deviceDataForChartList = useMemo(() => {
    if (!technical?.devices || technical.devices.length === 0) {
      return [];
    }
    return technical.devices.map((deviceItem) => ({
      name: deviceItem.label,
      value: deviceItem.percentage,
    }));
  }, [technical]);

  const hasGeographicDataFlag = useMemo(() => {
    return geographic?.countries && geographic.countries.length > 0;
  }, [geographic]);

  const hasTechnicalDataFlag = useMemo(() => {
    return deviceDataForChartList.length > 0;
  }, [deviceDataForChartList]);

  const chartColorPaletteList = ['#2C6CFF', '#B94746', '#FF6526'];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <Card className="p-6 bg-white border-neutral-100 rounded-[20px] flex flex-col gap-6 shadow-none h-full min-h-[440px]">
        <h3 className="text-xl font-medium text-dark leading-6">
          {t('overview.distributions.geographicTitle')}
        </h3>
        {hasGeographicDataFlag ? (
          <div className="max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
            <Table>
              <TableBody>
                {geographic?.countries.map((countryItem) => {
                  const FlagIconComponent = flags[countryItem.countryCode as keyof typeof flags];
                  return (
                    <TableRow
                      key={countryItem.countryCode}
                      className="border-none hover:bg-transparent"
                    >
                      <TableCell className="pl-0 py-3 w-[140px]">
                        <div className="flex items-center gap-3">
                          <div className="size-6 rounded-sm flex overflow-hidden shrink-0">
                            {FlagIconComponent ? (
                              <FlagIconComponent title={countryItem.countryCode} />
                            ) : (
                              <div className="w-6 h-6 bg-gray-200 rounded-sm flex items-center justify-center">
                                <CircleQuestionMark className="w-4 h-4 text-gray-500" />
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium text-dark truncate">
                            {countryItem.countryCode ?? t('overview.distributions.unknown')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-4">
                          <div className="h-2 w-full bg-neutral-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all duration-500"
                              style={{ width: `${countryItem.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-dark w-10 text-right">
                            {countryItem.percentage}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="pr-0 py-3 text-right text-sm text-secondary w-16">
                        {countryItem.count >= 1000
                          ? `${(countryItem.count / 1000).toFixed(1)}k`
                          : countryItem.count}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyAnalyticsState />
        )}
      </Card>

      <Card className="p-6 bg-white border-neutral-100 rounded-[20px] flex flex-col gap-6 h-full min-h-[440px] shadow-none">
        <h3 className="text-xl font-medium text-dark leading-6">
          {t('overview.distributions.devicesTitle')}
        </h3>

        {hasTechnicalDataFlag ? (
          <div className="flex-1 flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="relative size-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceDataForChartList}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {deviceDataForChartList.map((_entry, colorIndex) => (
                      <Cell
                        key={`cell-${colorIndex}`}
                        fill={chartColorPaletteList[colorIndex % chartColorPaletteList.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-col gap-4 w-full md:w-auto min-w-[160px]">
              {technical?.devices.map((deviceInfo, deviceIndex) => (
                <div key={deviceInfo.label} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="size-3 rounded-full"
                        style={{
                          backgroundColor:
                            chartColorPaletteList[deviceIndex % chartColorPaletteList.length],
                        }}
                      />
                      <span className="text-sm text-dark font-normal">{deviceInfo.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-dark">
                      {deviceInfo.percentage}%
                    </span>
                  </div>
                  <div className="h-px bg-neutral-100 w-full" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyAnalyticsState />
        )}
      </Card>
    </div>
  );
};
